import type { PortfolioInstrument, SecondOrderDomainKey } from '@akem/shared';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StockSearchBar } from '../../components/StockSearchBar.js';
import type { NewsArticle } from '../../services/perplexity.js';
import { newsApi } from '../../services/perplexity.js';
import type { StockSearchResult } from '../../services/polygon.js';
import { WidgetHost } from '../dashboard/WidgetHost.js';
import { StorylineChat } from '../intelligence/components/StorylineChat.tsx';
import {
  selectPortfolios,
  usePortfolioStore,
} from '../portfolios/portfolioStore.js';
import { IntelligenceGrid } from './components/IntelligenceGrid.js';
import { MarketPulseStrip } from './components/MarketPulseStrip.js';

const OVERVIEW_SYMBOLS = ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'META', 'TSLA'];
const INTELLIGENCE_DOMAINS: Array<{ id: SecondOrderDomainKey; label: string }> = [
  { id: 'marketTrends', label: 'Market Trends' },
  { id: 'politics', label: 'Politics' },
  { id: 'regulatory', label: 'Regulatory' },
  { id: 'mna', label: 'M&A' },
  { id: 'logistics', label: 'Logistics' },
  { id: 'customers', label: 'Customers' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'themes', label: 'Themes' },
];

const formatClockLabel = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
  return `${formatter.format(date)} ET`;
};

const formatDuration = (diffMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(hours)}H : ${pad(minutes)}M : ${pad(seconds)}S`;
};

const getNextOpen = (nowEastern: Date): Date => {
  const next = new Date(nowEastern);
  next.setHours(9, 30, 0, 0);
  if (nowEastern >= next) {
    next.setDate(next.getDate() + 1);
    next.setHours(9, 30, 0, 0);
  }
  while ([0, 6].includes(next.getDay())) {
    next.setDate(next.getDate() + 1);
    next.setHours(9, 30, 0, 0);
  }
  return next;
};

const getMarketTimer = (date: Date): { label: string; value: string } => {
  const nowEastern = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const open = new Date(nowEastern);
  open.setHours(9, 30, 0, 0);
  const close = new Date(nowEastern);
  close.setHours(16, 0, 0, 0);

  if (nowEastern >= open && nowEastern < close) {
    return { label: 'Market close', value: formatDuration(close.getTime() - nowEastern.getTime()) };
  }

  const nextOpen = getNextOpen(nowEastern);
  return { label: 'Market open', value: formatDuration(nextOpen.getTime() - nowEastern.getTime()) };
};

export const MarketDashboardPage: React.FC = () => {
  const [focusedSymbol, setFocusedSymbol] = React.useState<string>('SPY');
  const [globalNews, setGlobalNews] = React.useState<NewsArticle[] | null>(null);
  const [globalNewsError, setGlobalNewsError] = React.useState<string | null>(null);
  const [portfolioNews, setPortfolioNews] = React.useState<NewsArticle[] | null>(null);
  const [portfolioNewsError, setPortfolioNewsError] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<'all' | 'portfolio'>('portfolio');
  const [now, setNow] = React.useState(() => new Date());
  const navigate = useNavigate();
  const firstSelectionSeenRef = React.useRef(false);
  const lastSelectedSymbolRef = React.useRef<string | null>(null);

  const portfolios = usePortfolioStore(selectPortfolios);
  const defaultPortfolio = React.useMemo(
    () => portfolios.find((p) => p.isDefault) ?? portfolios[0] ?? null,
    [portfolios]
  );
  const addSymbols = usePortfolioStore((state) => state.addSymbols);
  const removeSymbol = usePortfolioStore((state) => state.removeSymbol);

  const [removingTickerIds, setRemovingTickerIds] = React.useState<Set<string>>(new Set());
  const [portfolioActionError, setPortfolioActionError] = React.useState<string | null>(null);

  const ownedSymbols = React.useMemo(() => {
    if (!defaultPortfolio) return [];
    return (
      defaultPortfolio.instruments
        ?.map((instrument: PortfolioInstrument) => instrument.symbol || instrument.displaySymbol || instrument.key)
        .filter((value: string | undefined): value is string => Boolean(value)) ?? []
    );
  }, [defaultPortfolio]);

  const portfolioSymbols = React.useMemo(
    () => (ownedSymbols.length > 0 ? ownedSymbols : OVERVIEW_SYMBOLS),
    [ownedSymbols]
  );

  const instruments = React.useMemo(
    () => defaultPortfolio?.instruments ?? [],
    [defaultPortfolio?.instruments]
  );

  const marketTimer = React.useMemo(() => getMarketTimer(now), [now]);

  const watchlistSymbols = React.useMemo(
    () => (viewMode === 'portfolio' ? portfolioSymbols : OVERVIEW_SYMBOLS),
    [portfolioSymbols, viewMode]
  );

  const ownedSymbolSet = React.useMemo(
    () => new Set(ownedSymbols.map((symbol) => symbol.toUpperCase())),
    [ownedSymbols]
  );

  React.useEffect(() => {
    if (watchlistSymbols.length === 0) return;
    if (!focusedSymbol || !watchlistSymbols.includes(focusedSymbol)) {
      setFocusedSymbol(watchlistSymbols[0]);
    }
  }, [watchlistSymbols, focusedSymbol]);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setGlobalNewsError(null);
        const data = await newsApi.getMarketNews({ limit: 12 });
        if (!cancelled) {
          setGlobalNews(data.articles || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[MarketDashboard] failed to load market news', err);
          setGlobalNews([]);
          setGlobalNewsError('Unable to load market news');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!defaultPortfolio) {
      setPortfolioNews(null);
      setPortfolioNewsError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setPortfolioNewsError(null);
        const data = await newsApi.getPortfolioNews(defaultPortfolio.id, { limit: 12 });
        if (!cancelled) {
          setPortfolioNews(data.articles || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[MarketDashboard] failed to load portfolio news', err);
          setPortfolioNews([]);
          setPortfolioNewsError('Unable to load portfolio news');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultPortfolio?.id]);

  const handleMarketOutputs = React.useCallback((outputs: Record<string, any>) => {
    const selected = outputs.selectedStock;
    if (selected && typeof selected.symbol === 'string') {
      const symbol = selected.symbol.toUpperCase();
      setFocusedSymbol(symbol);
      if (!firstSelectionSeenRef.current) {
        firstSelectionSeenRef.current = true;
        lastSelectedSymbolRef.current = symbol;
      } else if (lastSelectedSymbolRef.current !== symbol) {
        lastSelectedSymbolRef.current = symbol;
        navigate(`/profiles/${encodeURIComponent(symbol)}`);
      }
    }
  }, [navigate]);

  const handleSearchSelect = React.useCallback(
    (stock: StockSearchResult) => {
      const symbol = stock.symbol?.trim();
      if (symbol) {
        navigate(`/profiles/${encodeURIComponent(symbol.toUpperCase())}`);
      }
    },
    [navigate]
  );

  const handlePortfolioCardSelect = React.useCallback(
    (symbol: string) => {
      const normalized = symbol.trim().toUpperCase();
      if (!normalized) return;
      setFocusedSymbol(normalized);
      navigate(`/profiles/${encodeURIComponent(normalized)}`);
    },
    [navigate]
  );

  const handleAddStockToPortfolio = React.useCallback(
    async (stock: StockSearchResult) => {
      const symbol = stock.symbol?.toUpperCase();
      if (!symbol) {
        const message = 'Unable to add this ticker. Please try another search.';
        setPortfolioActionError(message);
        throw new Error(message);
      }
      if (!defaultPortfolio) {
        const message = 'Create a portfolio to start tracking tickers.';
        setPortfolioActionError(message);
        throw new Error(message);
      }
      if (ownedSymbolSet.has(symbol)) {
        setPortfolioActionError(null);
        return;
      }
      try {
        setPortfolioActionError(null);
        await addSymbols(defaultPortfolio.id, {
          symbols: [symbol],
          expectedVersion: defaultPortfolio.version,
        });
        setViewMode('portfolio');
      } catch (error: any) {
        console.warn('[MarketDashboard] Failed to add symbol', error);
        const message =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : 'Failed to add symbol');
        setPortfolioActionError(message);
        throw error;
      }
    },
    [addSymbols, defaultPortfolio, ownedSymbolSet, setViewMode]
  );

  const isSymbolInPortfolio = React.useCallback(
    (symbol: string) => {
      if (!symbol) {
        return false;
      }
      return ownedSymbolSet.has(symbol.toUpperCase());
    },
    [ownedSymbolSet]
  );

  const handleRemoveInstrument = React.useCallback(async (
    instrument: PortfolioInstrument
  ) => {
    const identifier = instrument.key || instrument.symbol || instrument.displaySymbol;
    if (!identifier) return;

    setRemovingTickerIds((prev) => new Set(prev).add(identifier));

    const state = usePortfolioStore.getState();
    const current = state.portfolios.find((p) => p.isDefault) ?? state.portfolios[0] ?? null;
    if (!current) {
      setRemovingTickerIds((prev) => {
        const next = new Set(prev);
        next.delete(identifier);
        return next;
      });
      return;
    }

    const mode = instrument.key ? 'key' : 'symbol';
    try {
      // Try with version for optimistic concurrency
      const updated = await removeSymbol(current.id, identifier, {
        mode,
        expectedVersion: current.version,
      });
      if (
        mode === 'key' &&
        updated.instruments.some((i) => i.key === instrument.key)
      ) {
        // Backend may not decode ':' in path on older deploys; try symbol fallback
        const sym = instrument.symbol || instrument.displaySymbol;
        if (sym) {
          try {
            await removeSymbol(current.id, sym, { mode: 'symbol' });
          } catch (e) {
            // swallow, main path already succeeded
          }
        }
      }
    } catch (error: any) {
      // If version conflict, retry without version
      const status = error?.response?.status ?? error?.status;
      if (status === 409) {
        try {
          await removeSymbol(current.id, identifier, { mode });
        } catch (e) {
          console.warn('[MarketDashboard] Remove retry failed', e);
        }
      } else {
        console.warn('[MarketDashboard] Failed to remove instrument', error);
      }
    } finally {
      setRemovingTickerIds((prev) => {
        const next = new Set(prev);
        next.delete(identifier);
        return next;
      });
    }
  }, [removeSymbol]);

  const handleRemoveSymbolFromStrip = React.useCallback(async (symbol: string) => {
    const instrument = instruments.find((item) => {
      const ident = item.symbol || item.displaySymbol || item.key;
      return ident?.toUpperCase() === symbol.toUpperCase();
    });
    if (instrument) {
      await handleRemoveInstrument(instrument);
    }
  }, [handleRemoveInstrument, instruments]);

  const overviewTicker = focusedSymbol || watchlistSymbols[0] || 'SPY';

  const intelligenceTiles = React.useMemo(
    () =>
      INTELLIGENCE_DOMAINS.map((domain) => ({
        ...domain,
        title: domain.label,
        profileIds: viewMode === 'portfolio' ? portfolioSymbols : [],
        viewMode,
      })),
    [portfolioSymbols, viewMode]
  );

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Standard Market Hours
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.primary', mt: 0.25 }}>
                {formatClockLabel(now)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                {marketTimer.label}
              </Typography>
              <Typography variant="h6" sx={{ color: 'error.main', mt: 0.25 }}>
                {marketTimer.value}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ width: '100%', maxWidth: 420 }}>
            <StockSearchBar
              size="medium"
              fullWidth
              clearOnSelect
              placeholder="Search companies or tickers..."
              onStockSelect={handleSearchSelect}
              onAddToPortfolio={handleAddStockToPortfolio}
              isInPortfolio={isSymbolInPortfolio}
            />
          </Box>
        </Stack>

        <MarketPulseStrip
          symbols={watchlistSymbols}
          selectedSymbol={overviewTicker}
          onSelectSymbol={handlePortfolioCardSelect}
          onRemoveSymbol={handleRemoveSymbolFromStrip}
          removingSymbols={removingTickerIds}
          removeEnabled={viewMode === 'portfolio' && Boolean(defaultPortfolio)}
        />

        {portfolioActionError && (
          <Alert severity="error" onClose={() => setPortfolioActionError(null)}>
            {portfolioActionError}
          </Alert>
        )}

        {/* Intelligence Section */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography 
              variant="h5" 
              fontWeight="600"
              sx={{
                textShadow: '0 0 40px rgba(184, 137, 255, 0.6), 0 0 80px rgba(184, 137, 255, 0.4)',
              }}
            >
              Akem Intelligence
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
              color="primary"
            >
              <ToggleButton value="portfolio">My Portfolio</ToggleButton>
              <ToggleButton value="all">All Market</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          
          <IntelligenceGrid tiles={intelligenceTiles} />

          <Box sx={{ mt: 3 }}>
            <StorylineChat
              ticker={viewMode === 'portfolio' ? overviewTicker : undefined}
              title="Ask Akem about portfolio storylines"
            />
          </Box>
        </Box>

        {/* Market Overview Section */}
        <Box>
          <Typography 
            variant="h5" 
            fontWeight="600" 
            sx={{ 
              mb: 2,
              textShadow: '0 0 40px rgba(184, 137, 255, 0.6), 0 0 80px rgba(184, 137, 255, 0.4)',
            }}
          >
            Market Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', p: 0 }}>
                  <WidgetHost
                    type="priceChart"
                    config={{ ticker: overviewTicker, title: `${overviewTicker} Price` }}
                    inputs={{ ticker: overviewTicker }}
                    disableCard
                    contentSx={{ p: 2, height: 400 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', p: 0 }}>
                  <WidgetHost
                    type="marketData"
                    config={{ title: 'Market Movers', symbols: portfolioSymbols }}
                    onOutputsChange={handleMarketOutputs}
                    disableCard
                    contentSx={{ p: 2, height: 400 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent sx={{ p: 0 }}>
                  <WidgetHost type="indicesOverview" disableCard contentSx={{ p: 2 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent sx={{ p: 0 }}>
                  <WidgetHost type="sectorOverview" disableCard contentSx={{ p: 2 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <WidgetHost type="calendar" disableCard contentSx={{ p: 2 }} />
            </Grid>
          </Grid>
        </Box>

        {/* News Section */}
        <Box>
          <Typography 
            variant="h5" 
            fontWeight="600" 
            sx={{ 
              mb: 2,
              textShadow: '0 0 40px rgba(184, 137, 255, 0.6), 0 0 80px rgba(184, 137, 255, 0.4)',
            }}
          >
            News & Sentiment
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: 0 }}>
                  <WidgetHost
                    type="newsSentiment"
                    inputs={{
                      recentNews:
                        viewMode === 'portfolio'
                          ? portfolioNews ?? undefined
                          : globalNews ?? undefined,
                    }}
                    contentSx={{ flex: 1, p: 2 }}
                    disableCard
                  />
                  {viewMode === 'portfolio' && portfolioNewsError && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="error">
                        {portfolioNewsError}
                      </Typography>
                    </Box>
                  )}
                  {viewMode === 'all' && globalNewsError && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="error">
                        {globalNewsError}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};
